import { COMET_PROXY_BASE } from './constants';

/**
 * Low-level JSON fetch for the Comet API. Mirrors the conventions in
 * `getPlayerSeasonStats.ts`: guards missing config, logs non-2xx with a
 * truncated body, catches network/parse failures, and returns `null` on any
 * failure so callers can render an empty/error state.
 *
 * @param path        Path relative to the API base, e.g. `/referee-assignments/competitions`.
 * @param baseUrl     Override base. Defaults to the env var (server-side). Browser
 *                    callers pass COMET_PROXY_BASE (`/api/comet`) to avoid CORS.
 * @param revalidate  Next.js ISR window in seconds; omit for uncached (dynamic) calls.
 */
export async function cometFetch<T>(
  path: string,
  { baseUrl, revalidate }: { baseUrl?: string; revalidate?: number } = {}
): Promise<T | null> {
  const base = baseUrl ?? process.env.NEXT_PUBLIC_COMET_DATA_API_BASE_URL;

  if (!base) {
    console.warn('[referee-assignments] skipped — missing base URL', {
      path,
      hint: 'set NEXT_PUBLIC_COMET_DATA_API_BASE_URL, or pass COMET_PROXY_BASE from the browser',
      proxyBase: COMET_PROXY_BASE,
    });
    return null;
  }

  const url = `${base}${path}`;

  try {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...(revalidate === undefined
        ? { cache: 'no-store' as const }
        : { next: { revalidate } }),
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '<unreadable body>');
      console.error('[referee-assignments] non-2xx response', {
        url,
        status: res.status,
        statusText: res.statusText,
        contentType: res.headers.get('content-type'),
        body: body.slice(0, 500),
      });
      return null;
    }

    return (await res.json()) as T;
  } catch (error) {
    console.error('[referee-assignments] fetch threw', {
      url,
      error:
        error instanceof Error
          ? { name: error.name, message: error.message }
          : error,
    });
    return null;
  }
}
